import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  RefreshButton,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { Iconify } from '../../../../../components/iconify';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import FilterGroup from '../../../components/FilterGroup';
import XTextInput from '../../../components/XTextInput';
import { Resources } from '../../resource';
import {
  reuploadCoinImageFromHyperliquid,
  type CoinImageExtension,
} from '../../../services/coinImagesService';

function MissingImageMarker({ label }: { label: string }) {
  return (
    <Chip
      size="small"
      label={label}
      color="warning"
      variant="outlined"
      sx={{ height: 24, fontSize: 11, fontWeight: 500 }}
    />
  );
}

function CopyUrlButton({ url }: { url?: string }) {
  const t = useTranslate();
  const notify = useNotify();
  if (!url) return null;
  return (
    <Tooltip title={t('module.coinSymbols.copyLink')}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(url);
          notify(t('module.coinSymbols.copySuccess'), { type: 'info' });
        }}
      >
        <Iconify icon="eva:copy-outline" width={18} />
      </IconButton>
    </Tooltip>
  );
}

function UrlActionButtons({ url }: { url?: string }) {
  const t = useTranslate();
  if (!url) return null;
  return (
    <>
      <CopyUrlButton url={url} />
      <Tooltip title={t('module.coinSymbols.openLink')}>
        <IconButton
          size="small"
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Iconify icon="eva:external-link-outline" width={18} />
        </IconButton>
      </Tooltip>
    </>
  );
}

function CoinImagePreview({
  url,
  alt,
  available,
  missingLabel,
  showActions = false,
}: {
  url?: string;
  alt?: string;
  available?: boolean;
  missingLabel: string;
  showActions?: boolean;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const missing = available === false || imgFailed || !url;

  if (missing) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <MissingImageMarker label={missingLabel} />
        {showActions && <UrlActionButtons url={url} />}
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        component="img"
        src={url}
        alt={alt}
        onError={() => setImgFailed(true)}
        sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
      />
      {showActions && <UrlActionButtons url={url} />}
    </Stack>
  );
}

function CoinSymbolRowActions({ record }: { record: Record<string, unknown> }) {
  const t = useTranslate();
  const notify = useNotify();
  const { refetch } = useListContext();
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmTypes, setConfirmTypes] = useState<CoinImageExtension[] | null>(null);

  const symbol = String(record?.symbol ?? '').trim();
  if (!symbol) return null;

  const typeLabel = (types: CoinImageExtension[]) => {
    if (types.length === 2) return t('module.coinSymbols.reuploadAll');
    return types[0] === 'svg' ? t('module.coinSymbols.reuploadSvg') : t('module.coinSymbols.reuploadPng');
  };

  const onReupload = async (types: CoinImageExtension[]) => {
    setLoading(true);
    try {
      await reuploadCoinImageFromHyperliquid(symbol, types);
      notify(t('module.coinSymbols.reuploadSuccess'), { type: 'success' });
      setConfirmTypes(null);
      await refetch();
    } catch (e: any) {
      const msg =
        e?.body?.message || e?.response?.data?.message || e?.message || t('module.coinSymbols.reuploadError');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionGuard resource="cdn-coin-symbols" action="update" fallback={<></>}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          disabled={loading}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          {t('module.coinSymbols.reupload')}
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmTypes(['svg', 'png']);
            }}
          >
            {t('module.coinSymbols.reuploadAll')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmTypes(['svg']);
            }}
          >
            {t('module.coinSymbols.reuploadSvg')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmTypes(['png']);
            }}
          >
            {t('module.coinSymbols.reuploadPng')}
          </MenuItem>
        </Menu>
      </Stack>

      <Dialog open={Boolean(confirmTypes)} onClose={() => !loading && setConfirmTypes(null)}>
        <DialogTitle>{t('module.coinSymbols.reuploadConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('module.coinSymbols.reuploadConfirmBody', {
              symbol,
              types: confirmTypes ? typeLabel(confirmTypes) : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTypes(null)} disabled={loading}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={loading || !confirmTypes}
            onClick={() => confirmTypes && onReupload(confirmTypes)}
          >
            {t('module.coinSymbols.reupload')}
          </Button>
        </DialogActions>
      </Dialog>
    </PermissionGuard>
  );
}

function ListActions() {
  return (
    <TopToolbar>
      <RefreshButton />
    </TopToolbar>
  );
}

export default function CoinSymbolsList() {
  const t = useTranslate();

  const filters = useMemo(
    () => [
      <FilterGroup key="coin-symbol-filters" alwaysOn spacing={2} p={{ xs: 0, sm: 1 }} pt={2}>
        <XTextInput
          key="symbol"
          source="symbol"
          label={t('module.coinSymbols.symbol')}
          alwaysOn
          sx={{ marginBottom: 0, minWidth: 200 }}
        />
      </FilterGroup>,
    ],
    [t]
  );

  return (
    <PermissionGuard resource="cdn-coin-symbols" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.coinSymbols.title')}
          links={[
            { name: t('resource.publishing'), href: '#' },
            { name: t('module.coinSymbols.title') },
          ]}
        />

        <Card variant="outlined" sx={{ mt: 2, p: 0 }}>
          <Box sx={{ px: 2, pt: 2, pb: 0, typography: 'body2', color: 'text.secondary' }}>
            {t('module.coinSymbols.listHint')}
          </Box>
          <List
            resource={Resources.coinSymbols}
            exporter={false}
            perPage={50}
            empty={false}
            filters={filters as any}
            actions={<ListActions />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="symbol" label={t('module.coinSymbols.symbol')} />
                <FunctionField
                  label={t('module.coinSymbols.hlPreview')}
                  render={(r: any) => (
                    <CoinImagePreview
                      url={r?.hycoinUrl}
                      alt={`${r?.symbol}-hl`}
                      available={r?.hlImageAvailable}
                      missingLabel={t('module.coinSymbols.hlMissing')}
                      showActions
                    />
                  )}
                />
                <FunctionField
                  label={t('module.coinSymbols.svgPreview')}
                  render={(r: any) => (
                    <CoinImagePreview
                      url={r?.svgUrl}
                      alt={`${r?.symbol}-svg`}
                      missingLabel={t('module.coinSymbols.cdnMissing')}
                      showActions
                    />
                  )}
                />
                <FunctionField
                  label={t('module.coinSymbols.pngPreview')}
                  render={(r: any) => (
                    <CoinImagePreview
                      url={r?.pngUrl}
                      alt={`${r?.symbol}-png`}
                      missingLabel={t('module.coinSymbols.cdnMissing')}
                      showActions
                    />
                  )}
                />
                <FunctionField
                  label={t('module.coinSymbols.actions')}
                  render={(r: any) => <CoinSymbolRowActions record={r} />}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
